import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { NO_DANGEROUS_FAILURE, NO_SAFE_FAILURE } from "const"
import Images from "assets/images"
import { SilSubsystem, SilSubsystemBlock } from "server/models"
import {
    StoreType,
    addModal,
    closeModal,
    setSilSafetyFunctionSubsystemList,
    setSilModelDevicesModelTarget,
    setSilModelDevicesOnSelect,

    initSilSubsystem,
    silArchitectureArray,
    silArchitectureBlockLength,
} from "client/store"
import {
    deepCopy,
    SilArchitecture,
    SilArchitecturalConstraints,
    SilCalculationMethod,
} from "client/utils"
import {
    Logo,
    SelectDropdown,
    SelectDropdownItem,
    Input,
} from "client/components"

import {
    ModelWrapper,
    ModelNestedWrapper,
    BlockWrapper,
    Block,
    BlockInputWrapper,
    ChangeButton,
    AddButton,
    RemoveButton,
    ModelSil,
} from "client/pages/sil/modelStyled"

import ModelDevices from "client/pages/sil/modelDevices"
import { getSilDeviceTitle, getModelSil } from "client/pages/sil/formulas"
import Settings from "./settings"

interface ModelProps {
    safetyFunctionId: number
    calculationMethodDangerous: SilCalculationMethod
    calculationMethodSafe: SilCalculationMethod
    architecturalConstraints: SilArchitecturalConstraints
}

const Model = ({
    // default data from safety function level
    safetyFunctionId,
    calculationMethodDangerous,
    calculationMethodSafe,
    architecturalConstraints,
}: ModelProps) => {
    const subsystemList = useSelector((state: StoreType) => state.silSafetyFunction.subsystemList)
    const moduleList = useSelector((state: StoreType) => state.module.moduleList)
    const userSettings = useSelector((state: StoreType) => state.system.userSettings)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [modelCreated, setModelCreated] = useState(false)

    const {
        iec615082010, // IEC 61508:2010
        iec615081998, // IEC 61508:1998
        isaTR842002, // ISA TR84:2002
        isaTR842022, // ISA TR84:2022
        vdivde21802019, // VDI/VDE 2180:2019
    } = userSettings.sil


    // calculation method can be or dangerous or safe or "no dangerous/safe failure", never both
    let calculationMethod: SilCalculationMethod = null
    if (calculationMethodDangerous !== NO_DANGEROUS_FAILURE) calculationMethod = calculationMethodDangerous
    if (calculationMethodSafe !== NO_SAFE_FAILURE) calculationMethod = calculationMethodSafe

    // create default empty subsystem
    const defaultArchitecture = silArchitectureArray[calculationMethod][0]
    const defaultBlockLength = silArchitectureBlockLength[defaultArchitecture]

    const emptySubsystem: SilSubsystem = {
        ...initSilSubsystem,
        architecture: defaultArchitecture,
        architecturalConstraints,

        iec615082010t1: iec615082010.t1,
        iec615082010mttr: iec615082010.mttr,
        iec615082010mrt: iec615082010.mrt,
        iec615082010beta: iec615082010.beta,
        iec615082010betaD: iec615082010.betaD,
        iec615082010k: iec615082010.k,

        iec615081998t1: iec615081998.t1,
        iec615081998mttr: iec615081998.mttr,
        iec615081998beta: iec615081998.beta,

        tr842002t1: isaTR842002.t1,
        tr842002mttr: isaTR842002.mttr,
        tr842002beta: isaTR842002.beta,
        tr842002safeFailure: isaTR842002.safeFailure,
        tr842002dangerousFailure: isaTR842002.dangerousFailure,

        vdivde21802019ptc0A: vdivde21802019.ptc0A,
        vdivde21802019ptc1A: vdivde21802019.ptc1A,
        vdivde21802019ptc0B: vdivde21802019.ptc0B,
        vdivde21802019ptc1B: vdivde21802019.ptc1B,
        vdivde21802019ptc0C: vdivde21802019.ptc0C,
        vdivde21802019ptc1C: vdivde21802019.ptc1C,
        vdivde21802019beta: vdivde21802019.beta,
        vdivde21802019t0: vdivde21802019.t0,
        vdivde21802019t1: vdivde21802019.t1,
        vdivde21802019t2: vdivde21802019.t2,

        isatr842022ti: isaTR842022.ti,
        isatr842022di: isaTR842022.di,
        isatr842022mttr: isaTR842022.mttr,
        isatr842022beta: isaTR842022.beta,
        isatr842022safeState: isaTR842022.safeState,

        blocks: new Array(defaultBlockLength).fill(null),
    }

    useEffect(() => {
        // create empty subsystem list for new safety function
        if (safetyFunctionId !== null) {
            setModelCreated(true)
            return
        }

        const emptySubsystemList: SilSubsystem[] = [
            { ...emptySubsystem, name: "Subsystem 1" },
            { ...emptySubsystem, name: "Subsystem 2" },
            { ...emptySubsystem, name: "Subsystem 3" },
        ]
        dispatch(setSilSafetyFunctionSubsystemList(emptySubsystemList))
        setModelCreated(true)
    }, [])

    useEffect(() => {
        if (!modelCreated) return // prevent "didMount" event

        // update subsystem architecture and blocks on change calculationMethod
        const newSubsystemList: SilSubsystem[] = deepCopy(subsystemList)
        const architectures = silArchitectureArray[calculationMethod]
        const architecture = architectures[0]
        const blockLength = silArchitectureBlockLength[architecture]

        newSubsystemList.forEach((subsystem, index) => {
            if (!architectures.includes(subsystem.architecture)) {
                const emptyArray = new Array(blockLength).fill(null)
                const extendedBlocks = subsystem.blocks.concat(emptyArray)

                newSubsystemList[index] = {
                    ...subsystem,
                    architecture,
                    blocks: extendedBlocks.slice(0, blockLength),
                }
            }
        })

        dispatch(setSilSafetyFunctionSubsystemList(newSubsystemList))
    }, [calculationMethod, modelCreated])

    const addSubsystem = (index: number) => {
        const arr1 = subsystemList.slice(0, index)
        const arr2 = subsystemList.slice(index)

        arr1.push({
            ...emptySubsystem,
            name: `Subsystem ${subsystemList.length + 1}`,
        })
        const newArr = arr1.concat(arr2)
        dispatch(setSilSafetyFunctionSubsystemList(newArr))
    }

    const removeSubsystem = (index: number) => {
        const arr1 = subsystemList.slice(0, index)
        const arr2 = subsystemList.slice(index + 1)
        const newArr = arr1.concat(arr2)
        dispatch(setSilSafetyFunctionSubsystemList(newArr))
    }

    const changeArchitecture = (architecture: SilArchitecture, index: number) => {
        const newSubsystemList: SilSubsystem[] = deepCopy(subsystemList)
        const blockLength = silArchitectureBlockLength[architecture]
        const emptyArray = new Array(blockLength).fill(null)
        const extendedBlocks = newSubsystemList[index].blocks.concat(emptyArray)

        newSubsystemList[index] = {
            ...newSubsystemList[index],
            architecture,
            blocks: extendedBlocks.slice(0, blockLength),
        }
        dispatch(setSilSafetyFunctionSubsystemList(newSubsystemList))
    }

    const changeName = (name: string, index: number) => {
        const newArr: SilSubsystem[] = deepCopy(subsystemList)
        newArr[index] = {
            ...newArr[index],
            name: name.slice(0, 255),
        }
        dispatch(setSilSafetyFunctionSubsystemList(newArr))
    }

    const changeBlock = (
        block: SilSubsystemBlock,
        subsystemIndex: number,
        blockIndex: number,
    ) => {
        const subsystems: SilSubsystem[] = deepCopy(subsystemList)
        const blocks: SilSubsystemBlock[] = deepCopy(subsystems[subsystemIndex].blocks)
        blocks[blockIndex] = block

        subsystems[subsystemIndex] = {
            ...subsystems[subsystemIndex],
            blocks,
        }

        dispatch(setSilSafetyFunctionSubsystemList(subsystems))
        dispatch(closeModal())
    }

    const changeDevice = (subsystemIndex: number, blockIndex: number) => {
        dispatch(setSilModelDevicesModelTarget("safetyFunction"))
        dispatch(setSilModelDevicesOnSelect((block) => changeBlock(block as SilSubsystemBlock, subsystemIndex, blockIndex)))
        dispatch(addModal({
            key: "modelDevices",
            content: <ModelDevices />,
        }))
    }

    return (
        <ModelWrapper direction="column">
            <ModelNestedWrapper>
                <AddButton onClick={() => addSubsystem(0)} />

                {
                    subsystemList.map((subsystem, subsystemIndex) => ([
                        <BlockWrapper key="blockWrapper" type="function">
                            <BlockInputWrapper>
                                <SelectDropdown title={subsystem.architecture}>
                                    {
                                        silArchitectureArray[calculationMethod].map((architecture) => (
                                            <SelectDropdownItem
                                                key={architecture}
                                                onClick={() => changeArchitecture(architecture, subsystemIndex)}
                                            >
                                                {architecture}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                                <Input
                                    type="text"
                                    value={subsystem.name}
                                    onChange={(e) => changeName(e.target.value, subsystemIndex)}
                                />
                            </BlockInputWrapper>

                            {
                                subsystem.blocks.map((block, blockIndex) => ([
                                    <Block
                                        key="block"
                                        title={getSilDeviceTitle(block && block.projectDevice, moduleList, lang)}
                                    >
                                        {
                                            block && block.projectDevice && (
                                                <Logo
                                                    size={100}
                                                    logo={
                                                        block.projectDevice.logo
                                                        || (block.projectDevice.qidProductId ? Images.qidProductLogo : Images.silDeviceLogo)
                                                    }
                                                />
                                            )
                                        }
                                        <ChangeButton onClick={() => changeDevice(subsystemIndex, blockIndex)}>
                                            {block && block.projectDevice ? lang.change : lang.add}
                                        </ChangeButton>
                                    </Block>,
                                    <ModelSil key="sil">
                                        {
                                            block
                                            && block.projectDevice
                                            && getModelSil(block.projectDevice, subsystem.architecturalConstraints, lang)
                                        }
                                    </ModelSil>,
                                ]))
                            }
                            <RemoveButton onClick={() => removeSubsystem(subsystemIndex)} />
                        </BlockWrapper>,
                        <AddButton
                            key="addButton"
                            onClick={() => addSubsystem(subsystemIndex + 1)}
                        />,
                    ]))
                }
            </ModelNestedWrapper>

            <Settings
                calculationMethodDangerous={calculationMethodDangerous}
                calculationMethodSafe={calculationMethodSafe}
            />
        </ModelWrapper>
    )
}
export default Model
